import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { IteratorRunMode } from '@/types/node';

export type IteratorRunModesByTargetNodeId = Record<string, IteratorRunMode | undefined>;

interface IteratorRunConfigState {
  runModesByWorkflowId: Record<string, IteratorRunModesByTargetNodeId | undefined>;
  setRunModeForTargets: (workflowId: string, targetNodeIds: string[], runMode: IteratorRunMode) => void;
  clearWorkflowRunModes: (workflowId: string) => void;
}

export const EMPTY_ITERATOR_RUN_MODES_BY_TARGET_NODE_ID: IteratorRunModesByTargetNodeId = Object.freeze({});

export const useIteratorRunConfigStore = create<IteratorRunConfigState>()(
  devtools(
    (set) => ({
      runModesByWorkflowId: {},
      setRunModeForTargets: (workflowId: string, targetNodeIds: string[], runMode: IteratorRunMode) =>
        set((state) => {
          const existingWorkflowModes =
            state.runModesByWorkflowId[workflowId] ?? EMPTY_ITERATOR_RUN_MODES_BY_TARGET_NODE_ID;
          const updatedWorkflowModes = {
            ...existingWorkflowModes,
            ...Object.fromEntries(targetNodeIds.map((targetNodeId) => [targetNodeId, runMode])),
          };

          return {
            runModesByWorkflowId: {
              ...state.runModesByWorkflowId,
              [workflowId]: updatedWorkflowModes,
            },
          };
        }),
      clearWorkflowRunModes: (workflowId: string) =>
        set((state) => {
          const { [workflowId]: _, ...rest } = state.runModesByWorkflowId;
          return { runModesByWorkflowId: rest };
        }),
    }),
    {
      name: 'Iterator Run Config Store',
      enabled: import.meta.env.DEV,
    },
  ),
);
